import { CalendarEvent } from "../types/event";
import { groupsOf } from "./eventFacets";

/**
 * The chips a row or a card shows beside an event.
 *
 * The design calls for chips reading Area, Access, School section and a timing
 * band ("Sport", "Open to all", "Year 7-12", "After school"). NONE of those has
 * a backing field on the S2 event document, which is the same wall eventFacets
 * records for the filter rail: a chip that states something the data does not
 * know is worse than no chip. So these are the things an event DOES carry, in
 * the same shape.
 *
 *   Event type   `EventType`        the ten values in useEventPublish
 *   Approval     `Approval.Required`
 *   Permission   `ActiveAt`         the switch that shows carers the permission
 *   Audience     `Groups`           HOW MANY groups, with the names on hover
 *   Tags         `Tags`             the first few, then a count of the rest
 *
 * Tones are cc-ui's semantic ones rather than hand-picked hex, so the theme
 * owns the palette (see CLAUDE.md on the retired .em-pill). The category colour
 * is not a chip: it is the accent bar down the side of the row, which is how
 * the calendar's own labels are tinted.
 */
export type ChipTone = "neutral" | "info" | "success" | "warning";

export interface EventChip {
	/** Unique within one event, so React has a stable key. */
	id: string;
	label: string;
	tone: ChipTone;
	/**
	 * What the chip is standing in for, one entry per line of the hover card.
	 *
	 * A LIST rather than a joined string, because the views render it through
	 * `Tooltip`'s `details` node and a run of names separated by commas is not
	 * something anyone reads. Set on the audience chip, whose label is a count
	 * and whose hover is the names it counted.
	 */
	detail?: string[];
	/**
	 * What KIND of chip this is, for the views that draw it differently.
	 *
	 * A real field rather than sniffing the `id` prefix: the ids exist to be
	 * unique React keys, and a view keying its styling off their shape would
	 * break silently the first time one was renamed.
	 *
	 * Only tags carry one. The others say something about the event's state —
	 * its type, whether approval is required, how many groups — and are drawn
	 * as plain badges; a tag is a label somebody stuck on, which is why it is
	 * drawn as one.
	 *
	 * `tag-more` is the overflow count, which is shaped like a tag but is not
	 * one: it reads "+2", so it must not take the `#` the named ones get.
	 */
	kind?: "tag" | "tag-more";
}

/** Tags named in full before the rest become a count. See the note below. */
const MAX_NAMED_TAGS = 3;

export function chipsFor(evt: CalendarEvent): EventChip[] {
	const chips: EventChip[] = [];

	const type = (evt.EventType ?? "").trim();
	if (type) chips.push({ id: `type:${type}`, label: type, tone: "info" });

	if (evt.Approval?.Required === true) {
		chips.push({
			id: "approval:required",
			label: "Approval required",
			tone: "warning",
		});
	}

	/* NO CHIP FOR `ActiveAt`. There used to be a green "Permission published",
	   on the reasoning that the published state was the informative half of the
	   pair and "not published yet" was not worth a chip. Neither half is here
	   now: released-to-carers is a question about where an event sits in the
	   sign-off process, which the event's own panel answers properly, and a
	   third chip beside the type and the approval state was spending a row's
	   width to answer it badly.

	   Nor does the rail: its Approval facet ("Approval required", "Compulsory",
	   "Medical check", "Emergency contact", "Permission published") was removed
	   too, so the event's own panel is now the only place the question is
	   asked. */

	/* ONE CHIP FOR THE AUDIENCE, counting rather than naming.
	   The groups used to get a chip each, two of them, then a "+N more". Their
	   real labels run to "8 Japanese Course (2026) - Parents", so they were cut
	   to 30 characters to keep a badge from setting the row's width — which
	   left two truncated names and a number, and a reader still had to hover to
	   learn anything. A count says the same thing in a fifth of the space and
	   is honest about being a summary.

	   The names go on the chip's `detail`, which both views render through the
	   app's own Tooltip as one name per line. */
	const groups = groupsOf(evt);
	if (groups.length > 0) {
		chips.push({
			id: "groups",
			label: `${groups.length} group${groups.length === 1 ? "" : "s"}`,
			tone: "neutral",
			detail: groups.map((g) => g.label),
		});
	}

	/* TAGS ARE NAMED, NOT COUNTED — the opposite of the audience chip above, and
	   for the opposite reason. A group's label runs to "8 Japanese Course (2026)
	   - Parents", so naming two of them set the row's width and still told the
	   reader nothing. A tag is short by construction (the API caps one at 40
	   characters, and they run to "Sport", "Year 7", "Boarders"), and the whole
	   point of putting one on an event is to recognise it at a glance. A count
	   would answer "this event has been tagged", which nobody asked.

	   Capped at three so a heavily tagged event still cannot set the width, with
	   the remainder rolled into one chip that names them on hover. */
	const tags = (evt.Tags ?? []).filter((tag) => tag.trim());
	for (const tag of tags.slice(0, MAX_NAMED_TAGS)) {
		chips.push({ id: `tag:${tag}`, label: tag, tone: "neutral", kind: "tag" });
	}
	if (tags.length > MAX_NAMED_TAGS) {
		const rest = tags.slice(MAX_NAMED_TAGS);
		chips.push({
			id: "tags:more",
			label: `+${rest.length}`,
			tone: "neutral",
			detail: rest,
			kind: "tag-more",
		});
	}

	return chips;
}

/**
 * The extra class a chip needs, or undefined for the plain badge.
 *
 * Lives here beside `chipsFor` so the two views cannot drift into drawing the
 * same chip differently — they render an identical list and this is the only
 * thing that varies between them.
 */
export function chipClass(chip: EventChip): string | undefined {
	if (chip.kind === "tag") return "em-chip-tag";
	// Same shape, but the modifier suppresses the `#` — see the CSS.
	if (chip.kind === "tag-more") return "em-chip-tag em-chip-tag--more";
	return undefined;
}
